import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowDown, ArrowRight, ArrowUp, Check, Clock, LayoutDashboard, Loader2, MessageSquare, Plus, Users, X } from 'lucide-react';
import { useAuth } from '@/components/auth/AuthContext';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Seo } from '@/components/seo/Seo';
import { supabase } from '@/integrations/supabase/client';
import { loadLensJsonProperties } from '@/lib/lens-json-properties';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import {
  MAX_CONVERSATION_METHODS,
  buildConversationMethodsPayload,
  getConversationBoardName,
  isExpertBoardConversation,
  resolveConversationMethodOrder,
  uniqueConversationMethodIds,
} from '@/lib/conversation-methods';
import { getIndustryMenuLabel, groupByIndustry } from '@/lib/industries';

type ConversationRow = {
  id: string;
  thinking_method_id: string | null;
  openai_conversation_id: string | null;
  created_at: string;
  updated_at: string;
};

type LensRow = {
  id: string;
  name: string;
  author: string;
  bio: string | null;
  icon_url?: string | null;
  status?: string | null;
  author_email?: string | null;
  order_index?: number | null;
  industry?: string;
  header?: string;
  teaser?: string;
  skills?: string[];
};

type LensVisibilityProfile = {
  is_admin?: boolean | null;
  email?: string | null;
};

type ExpertBoard = {
  id: string;
  name: string | null;
  updated_at: string;
  created_at: string;
  lenses: LensRow[];
};

const BOARD_DISPLAY_LIMIT = 80;
const CONVERSATION_PAGE_SIZE = 200;

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

const getInitials = (value: string) =>
  value
    .split(' ')
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

const isVisibleLensForProfile = (
  method: Pick<LensRow, 'status' | 'author_email'>,
  profile: LensVisibilityProfile | null,
) => {
  const isAdmin = profile?.is_admin === true;
  const userEmail = (profile?.email || '').toLowerCase();
  const authorEmail = (method.author_email || '').toLowerCase();

  switch (method.status) {
    case 'draft':
      return false;
    case 'admins':
      return isAdmin;
    case 'author':
      return isAdmin || (!!authorEmail && authorEmail === userEmail);
    case 'published':
      return true;
    default:
      return false;
  }
};

const ExpertBoards = () => {
  const { user, loading: authLoading, isAnonymous } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [boards, setBoards] = useState<ExpertBoard[]>([]);
  const [availableLenses, setAvailableLenses] = useState<LensRow[]>([]);
  const [lensesLoaded, setLensesLoaded] = useState(false);
  const [lensesLoading, setLensesLoading] = useState(false);
  const [boardDialogOpen, setBoardDialogOpen] = useState(false);
  const [selectedLensIds, setSelectedLensIds] = useState<string[]>([]);
  const [boardNameInput, setBoardNameInput] = useState('');
  const [creatingBoard, setCreatingBoard] = useState(false);
  const [loading, setLoading] = useState(true);
  const creatingBoardRef = useRef(false);
  const userId = user?.id;

  const loadVisibleLenses = useCallback(async () => {
    if (!userId) return;
    setLensesLoading(true);
    try {
      const { data: methodsData, error: methodsError } = await supabase
        .from('thinking_methods')
        .select('id, name, author, bio, icon_url, status, author_email, order_index')
        .order('order_index', { ascending: true });

      if (methodsError) throw methodsError;

      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('is_admin, email')
        .eq('user_id', userId)
        .maybeSingle();

      if (profileError && profileError.code !== 'PGRST116') throw profileError;

      const filteredMethods = ((methodsData || []) as LensRow[])
        .filter((method) => isVisibleLensForProfile(method, profileData));
      const methodIds = filteredMethods.map((method) => method.id);

      const attrsRes = methodIds.length > 0
        ? await loadLensJsonProperties(methodIds)
        : { data: [], error: null };

      if (attrsRes.error) throw attrsRes.error;

      const propsByLensId = new Map<string, Partial<LensRow>>();
      for (const attr of attrsRes.data || []) {
        try {
          const parsed = JSON.parse(attr.attribute_value);
          propsByLensId.set(attr.thinking_method_id, {
            industry: parsed.industry || undefined,
            header: parsed.header || undefined,
            teaser: parsed.teaser || undefined,
            skills: Array.isArray(parsed.skills) ? parsed.skills : undefined,
          });
        } catch (error) {
          console.warn('Ignoring invalid lens JSON properties for Expert Board creation:', error);
        }
      }

      setAvailableLenses(
        filteredMethods.map((method) => ({
          ...method,
          status: method.status || 'draft',
          ...propsByLensId.get(method.id),
        })),
      );
      setLensesLoaded(true);
    } catch (error) {
      console.error('Error loading Experts for Expert Board creation:', error);
      toast({
        title: 'Error',
        description: 'Failed to load Experts',
        variant: 'destructive',
      });
    } finally {
      setLensesLoading(false);
    }
  }, [toast, userId]);

  const fetchBoards = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const boardConversations: ConversationRow[] = [];
      let from = 0;
      let hasMore = true;

      while (hasMore && boardConversations.length < BOARD_DISPLAY_LIMIT) {
        const to = from + CONVERSATION_PAGE_SIZE - 1;
        const { data: conversationsData, error: conversationsError } = await supabase
          .from('conversations')
          .select('id, thinking_method_id, openai_conversation_id, created_at, updated_at')
          .eq('user_id', userId)
          .order('updated_at', { ascending: false })
          .range(from, to);

        if (conversationsError) throw conversationsError;

        const page = (conversationsData || []) as ConversationRow[];
        boardConversations.push(
          ...page.filter((conversation) => isExpertBoardConversation(conversation)),
        );

        hasMore = page.length === CONVERSATION_PAGE_SIZE;
        from += CONVERSATION_PAGE_SIZE;
      }

      const visibleBoardConversations = boardConversations.slice(0, BOARD_DISPLAY_LIMIT);

      if (visibleBoardConversations.length === 0) {
        setBoards([]);
        return;
      }

      const boardLensOrders = new Map<string, string[]>();
      for (const conversation of visibleBoardConversations) {
        boardLensOrders.set(
          conversation.id,
          resolveConversationMethodOrder(
            conversation.openai_conversation_id,
            conversation.thinking_method_id,
          ),
        );
      }

      const lensIds = Array.from(new Set(
        Array.from(boardLensOrders.values()).flat(),
      ));

      const [methodsRes, attrsRes] = await Promise.all([
        supabase
          .from('thinking_methods')
          .select('id, name, author, bio, icon_url')
          .in('id', lensIds),
        loadLensJsonProperties(lensIds),
      ]);

      if (methodsRes.error) throw methodsRes.error;
      if (attrsRes.error) throw attrsRes.error;

      const propsByLensId = new Map<string, Partial<LensRow>>();
      for (const attr of attrsRes.data || []) {
        try {
          const parsed = JSON.parse(attr.attribute_value);
          propsByLensId.set(attr.thinking_method_id, {
            industry: parsed.industry || undefined,
            header: parsed.header || undefined,
            teaser: parsed.teaser || undefined,
            skills: Array.isArray(parsed.skills) ? parsed.skills : undefined,
          });
        } catch (error) {
          console.warn('Ignoring invalid lens JSON properties for Expert Boards:', error);
        }
      }

      const lensById = new Map<string, LensRow>();
      for (const method of methodsRes.data || []) {
        lensById.set(method.id, {
          ...method,
          ...propsByLensId.get(method.id),
        });
      }

      setBoards(
        visibleBoardConversations
          .map((conversation) => {
            const order = boardLensOrders.get(conversation.id) || [];
            return {
              id: conversation.id,
              name: getConversationBoardName(conversation.openai_conversation_id),
              updated_at: conversation.updated_at,
              created_at: conversation.created_at,
              lenses: order.map((lensId) => lensById.get(lensId)).filter((lens): lens is LensRow => !!lens),
            };
          }),
      );
    } catch (error) {
      console.error('Error loading expert boards:', error);
      toast({
        title: 'Error',
        description: 'Failed to load Expert Boards',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast, userId]);

  useEffect(() => {
    if (authLoading) return;
    if (!userId || isAnonymous) {
      setBoards([]);
      setAvailableLenses([]);
      setLensesLoaded(false);
      setLoading(false);
      return;
    }

    void fetchBoards();
    void loadVisibleLenses();
  }, [authLoading, fetchBoards, isAnonymous, loadVisibleLenses, userId]);

  const boardCountLabel = useMemo(
    () => `${boards.length} ${boards.length === 1 ? 'Board' : 'Boards'}`,
    [boards.length],
  );

  const selectedLensIdSet = useMemo(() => new Set(selectedLensIds), [selectedLensIds]);
  const selectedLensDetails = useMemo(
    () => selectedLensIds
      .map((id) => availableLenses.find((lens) => lens.id === id))
      .filter((lens): lens is LensRow => !!lens),
    [availableLenses, selectedLensIds],
  );
  const groupedAvailableLenses = useMemo(() => {
    return groupByIndustry(
      availableLenses.filter((lens) => !selectedLensIdSet.has(lens.id)),
    );
  }, [availableLenses, selectedLensIdSet]);
  const trimmedBoardName = boardNameInput.trim();
  const canCreateBoard =
    trimmedBoardName.length > 0 &&
    selectedLensIds.length >= 2 &&
    selectedLensIds.length <= MAX_CONVERSATION_METHODS &&
    !creatingBoard;

  const openBoardCreationDialog = () => {
    setSelectedLensIds([]);
    setBoardNameInput('');
    setBoardDialogOpen(true);
    if (!lensesLoaded && !lensesLoading) {
      void loadVisibleLenses();
    }
  };

  const handleBoardDialogOpenChange = (open: boolean) => {
    if (creatingBoardRef.current) return;
    setBoardDialogOpen(open);
    if (open) {
      setSelectedLensIds([]);
      setBoardNameInput('');
      if (!lensesLoaded && !lensesLoading) {
        void loadVisibleLenses();
      }
    }
  };

  const addSelectedLens = (lensId: string) => {
    if (creatingBoard) return;
    setSelectedLensIds((prev) => {
      const next = uniqueConversationMethodIds([...prev, lensId]);
      return next.length > MAX_CONVERSATION_METHODS ? prev : next;
    });
  };

  const removeSelectedLens = (lensId: string) => {
    if (creatingBoard) return;
    setSelectedLensIds((prev) => prev.filter((id) => id !== lensId));
  };

  const moveSelectedLens = (lensId: string, direction: -1 | 1) => {
    if (creatingBoard) return;
    setSelectedLensIds((prev) => {
      const index = prev.indexOf(lensId);
      const targetIndex = index + direction;
      if (index === -1 || targetIndex < 0 || targetIndex >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      return next;
    });
  };

  const createBoard = async () => {
    if (!userId || creatingBoardRef.current) return;

    const methodIds = uniqueConversationMethodIds(selectedLensIds);
    if (methodIds.length < 2) return;
    if (methodIds.length > MAX_CONVERSATION_METHODS) return;

    const boardName = boardNameInput.trim();
    if (!boardName) {
      toast({
        title: 'Board name required',
        description: 'Enter a name for the Expert Board.',
        variant: 'destructive',
      });
      return;
    }

    creatingBoardRef.current = true;
    setCreatingBoard(true);
    try {
      const { data, error } = await supabase
        .from('conversations')
        .insert({
          user_id: userId,
          thinking_method_id: methodIds[0],
          openai_conversation_id: buildConversationMethodsPayload(methodIds, { name: boardName }),
        })
        .select('id')
        .single();

      if (error || !data) {
        throw error ?? new Error('Failed to create Expert Board');
      }

      setBoardDialogOpen(false);
      setSelectedLensIds([]);
      setBoardNameInput('');
      navigate(`/board/${data.id}`);
    } catch (error) {
      console.error('Error creating Expert Board:', error);
      toast({
        title: 'Error',
        description: 'Failed to create Expert Board',
        variant: 'destructive',
      });
    } finally {
      creatingBoardRef.current = false;
      setCreatingBoard(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="flex min-h-[60vh] items-center justify-center">
          <div className="text-center space-y-4">
            <LayoutDashboard className="h-12 w-12 text-primary mx-auto animate-pulse" />
            <p className="text-muted-foreground">Loading Expert Boards...</p>
          </div>
        </main>
      </div>
    );
  }

  const needsAccount = !user || isAnonymous;

  return (
    <>
      <Seo
        title="Applyt: Expert Boards"
        description="Boards where selected Experts answer together in order."
      />
      <div className="min-h-screen bg-[hsl(var(--brand-accent-warm-bg))]/30 text-foreground font-sans">
        <Header />

        <main>
          <section className="border-b border-[hsl(var(--brand-accent-warm-text))]/20 bg-card/95 px-6 py-12 shadow-sm">
            <div className="mx-auto flex max-w-6xl flex-col gap-6 md:flex-row md:items-end md:justify-between">
              <div className="max-w-2xl">
                <div className="mb-3 inline-flex items-center gap-2 rounded-md border border-[hsl(var(--brand-accent-warm-text))]/30 bg-[hsl(var(--brand-accent-warm-bg))] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--brand-accent-warm-text))]">
                  <Users className="h-3.5 w-3.5" />
                  Executive workspace
                </div>
                <h1
                  className="text-3xl font-bold leading-tight md:text-5xl"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  Expert Boards
                </h1>
              </div>
              {!needsAccount && (
                <div className="flex items-center gap-3">
                  <div className="text-sm text-muted-foreground">{boardCountLabel}</div>
                  <Button onClick={openBoardCreationDialog} className="gap-2">
                    <Plus className="h-4 w-4" />
                    New Expert Board
                  </Button>
                </div>
              )}
            </div>
          </section>

          <section className="px-6 py-10">
            <div className="mx-auto max-w-6xl">
              {needsAccount ? (
                <div className="rounded-lg border border-border/60 bg-card p-8 text-center">
                  <LayoutDashboard className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                  <h2 className="text-xl font-semibold">Sign in to use Expert Boards</h2>
                  <p className="mx-auto mt-2 max-w-lg text-sm leading-6 text-muted-foreground">
                    Boards are saved workspaces. Sign in to create and reopen Expert Boards.
                  </p>
                  <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
                    <Button onClick={() => navigate('/auth')}>Sign in</Button>
                    <Button variant="outline" onClick={() => navigate('/lenses')}>
                      Browse Experts
                    </Button>
                  </div>
                </div>
              ) : boards.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border/70 bg-muted/20 p-8 text-center">
                  <MessageSquare className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                  <h2 className="text-xl font-semibold">Create your first Expert Board</h2>
                  <p className="mx-auto mt-2 max-w-lg text-sm leading-6 text-muted-foreground">
                    Name the Board and pick the Experts who should work together on it.
                  </p>
                  <Button onClick={openBoardCreationDialog} className="mt-6 gap-2">
                    <Plus className="h-4 w-4" />
                    New Expert Board
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                  {boards.map((board) => (
                    <button
                      key={board.id}
                      type="button"
                      onClick={() => navigate(`/board/${board.id}`)}
                      className="group flex min-h-[280px] flex-col overflow-hidden rounded-lg border border-[hsl(var(--brand-accent-warm-text))]/25 bg-card text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-[hsl(var(--brand-accent-warm-text))]/50 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[hsl(var(--brand-accent-warm-text))]/40"
                    >
                      <div className="border-b border-[hsl(var(--brand-accent-warm-text))]/20 bg-[hsl(var(--brand-accent-warm-bg))]/50 p-5">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex min-w-0 items-center gap-2.5">
                            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border border-[hsl(var(--brand-accent-warm-text))]/25 bg-card text-[hsl(var(--brand-accent-warm-text))]">
                              <LayoutDashboard className="h-4 w-4" />
                            </span>
                            <h3 className="line-clamp-1 text-base font-semibold text-foreground">
                              {board.name || 'Untitled Board'}
                            </h3>
                          </div>
                          <div className="flex flex-shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
                            <Clock className="h-3.5 w-3.5" />
                            {timeAgo(board.updated_at)}
                          </div>
                        </div>
                      </div>

                      <div className="flex-1 p-5">
                        <div className="mb-4 flex items-center justify-between gap-4">
                          <div className="flex -space-x-2">
                            {board.lenses.slice(0, 3).map((lens) => (
                              <Avatar key={lens.id} className="h-9 w-9 border-2 border-card ring-1 ring-[hsl(var(--brand-accent-warm-text))]/40">
                                <AvatarImage src={lens.icon_url || undefined} alt={lens.name} />
                                <AvatarFallback className="bg-[hsl(var(--brand-accent-warm-bg))] text-[hsl(var(--brand-accent-warm-text))] text-[11px] font-semibold">
                                  {getInitials(lens.name)}
                                </AvatarFallback>
                              </Avatar>
                            ))}
                          </div>
                          <span className="rounded-md bg-[hsl(var(--brand-accent-warm-bg))] px-2.5 py-1 text-xs font-semibold text-[hsl(var(--brand-accent-warm-text))]">
                            {board.lenses.length} {board.lenses.length === 1 ? 'Expert' : 'Experts'}
                          </span>
                        </div>

                        <div className="space-y-3">
                          {board.lenses.map((lens, index) => (
                            <div key={lens.id} className="flex gap-3">
                              <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border border-[hsl(var(--brand-accent-warm-text))]/30 bg-[hsl(var(--brand-accent-warm-bg))]/60 text-[11px] font-semibold text-[hsl(var(--brand-accent-warm-text))]">
                                {index + 1}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                                  <span className="font-medium text-foreground">{lens.author}</span>
                                  <span className="text-xs text-muted-foreground">{lens.name}</span>
                                </div>
                                <div className="mt-1 flex flex-wrap gap-1.5">
                                  {(lens.industry ? [lens.industry] : [])
                                    .concat((lens.skills || []).slice(0, 2))
                                    .slice(0, 3)
                                    .map((label) => (
                                      <span key={label} className="rounded-md bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                                        {label}
                                      </span>
                                    ))}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="flex items-center justify-between border-t border-[hsl(var(--brand-accent-warm-text))]/20 bg-[hsl(var(--brand-accent-warm-bg))]/30 px-5 py-3 text-sm font-medium text-[hsl(var(--brand-accent-warm-text))]">
                        Open Expert Board
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
      <Dialog open={boardDialogOpen} onOpenChange={handleBoardDialogOpenChange}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-hidden p-0">
          <DialogHeader className="border-b border-border/60 px-6 py-5">
            <DialogTitle>Create Expert Board</DialogTitle>
            <DialogDescription>
              Name the Board and pick the Experts who should answer together.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 border-b border-border/60 px-6 py-4">
            <Label htmlFor="board-name-input" className="text-sm font-medium">
              Board name
            </Label>
            <Input
              id="board-name-input"
              value={boardNameInput}
              onChange={(event) => setBoardNameInput(event.target.value)}
              placeholder="e.g. Q3 Pricing Review"
              maxLength={120}
              autoFocus
              required
              disabled={creatingBoard}
            />
          </div>

          <div className="grid min-h-0 gap-0 md:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
            <section className="min-h-0 border-b border-border/60 p-4 md:border-b-0 md:border-r">
              <div className="mb-3 flex items-center justify-between gap-3">
                <h3 className="text-sm font-semibold">Selected Experts</h3>
                <span className="text-xs text-muted-foreground">
                  {selectedLensIds.length}/{MAX_CONVERSATION_METHODS}
                </span>
              </div>

              {selectedLensDetails.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border/70 bg-muted/25 p-4 text-sm text-muted-foreground">
                  No Experts selected
                </div>
              ) : (
                <div className="max-h-[44vh] space-y-2 overflow-y-auto pr-1">
                  {selectedLensDetails.map((lens, index) => {
                    const moveUpDisabled = creatingBoard || index === 0;
                    const moveDownDisabled = creatingBoard || index === selectedLensDetails.length - 1;

                    return (
                      <div key={lens.id} className="flex items-center gap-2 rounded-lg border border-border/60 bg-card px-2 py-2">
                        <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border border-border/70 bg-background text-[11px] font-semibold text-muted-foreground">
                          {index + 1}
                        </div>
                        <Avatar className="h-8 w-8 flex-shrink-0">
                          <AvatarImage src={lens.icon_url || undefined} alt={lens.name} />
                          <AvatarFallback className="bg-primary/10 text-primary text-[11px] font-semibold">
                            {getInitials(lens.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="truncate text-sm font-medium text-foreground">{lens.author}</span>
                            {index === 0 && (
                              <span className="rounded-md bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                                Primary
                              </span>
                            )}
                          </div>
                          <p className="truncate text-xs text-muted-foreground">{lens.name}</p>
                        </div>
                        <div className="flex flex-shrink-0 items-center gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            disabled={moveUpDisabled}
                            onClick={() => moveSelectedLens(lens.id, -1)}
                            aria-label={`Move ${lens.name} up`}
                          >
                            <ArrowUp className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            disabled={moveDownDisabled}
                            onClick={() => moveSelectedLens(lens.id, 1)}
                            aria-label={`Move ${lens.name} down`}
                          >
                            <ArrowDown className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 hover:text-destructive"
                            disabled={creatingBoard}
                            onClick={() => removeSelectedLens(lens.id)}
                            aria-label={`Remove ${lens.author}`}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            <section className="min-h-0 p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <h3 className="text-sm font-semibold">Available Experts</h3>
                {lensesLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
              </div>

              {lensesLoading && availableLenses.length === 0 ? (
                <div className="rounded-lg border border-border/60 bg-muted/20 p-4 text-sm text-muted-foreground">
                  Loading Experts...
                </div>
              ) : availableLenses.length === 0 ? (
                <div className="rounded-lg border border-border/60 bg-muted/20 p-4 text-sm text-muted-foreground">
                  No visible Experts are available.
                </div>
              ) : selectedLensIds.length >= MAX_CONVERSATION_METHODS ? (
                <div className="rounded-lg border border-border/60 bg-muted/20 p-4 text-sm text-muted-foreground">
                  A Board can have up to {MAX_CONVERSATION_METHODS} Experts.
                </div>
              ) : groupedAvailableLenses.length === 0 ? (
                <div className="rounded-lg border border-border/60 bg-muted/20 p-4 text-sm text-muted-foreground">
                  No more Experts available.
                </div>
              ) : (
                <div className="max-h-[44vh] space-y-4 overflow-y-auto pr-1">
                  {groupedAvailableLenses.map(([industry, lenses]) => (
                    <div key={industry}>
                      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {getIndustryMenuLabel(industry)}
                      </div>
                      <div className="space-y-1.5">
                        {lenses.map((lens) => (
                          <button
                            key={lens.id}
                            type="button"
                            disabled={creatingBoard}
                            onClick={() => addSelectedLens(lens.id)}
                            className="flex w-full items-center gap-3 rounded-lg border border-transparent px-2 py-2 text-left transition-colors hover:border-border/70 hover:bg-muted/60 focus:outline-none focus:ring-2 focus:ring-primary/35 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <Avatar className="h-8 w-8 flex-shrink-0">
                              <AvatarImage src={lens.icon_url || undefined} alt={lens.name} />
                              <AvatarFallback className="bg-primary/10 text-primary text-[11px] font-semibold">
                                {getInitials(lens.name)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-medium text-foreground">{lens.author}</span>
                              <span className="block truncate text-xs text-muted-foreground">{lens.name}</span>
                            </span>
                            <Plus className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>

          <DialogFooter className="items-center gap-3 border-t border-border/60 px-6 py-4 sm:justify-between sm:space-x-0">
            <p className="text-xs text-muted-foreground">
              {!trimmedBoardName
                ? 'Enter a Board name'
                : selectedLensIds.length < 2
                  ? 'Choose at least two Experts'
                  : 'Experts answer in selected order'}
            </p>
            <Button onClick={createBoard} disabled={!canCreateBoard} className="gap-2">
              {creatingBoard ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              Create Board
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ExpertBoards;
